import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchTransaction, type AdminTransactionDetail } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, RelativeTime } from '@/components/DataDisplay'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A10.02 — Tranzaksiya kartasi. GET /admin/transactions/:id (backend `ad54e3c`).
 */
export function TransactionCard() {
  const t = useT()
  const { can } = useAuth()
  const { id = '' } = useParams()
  const [tx, setTx] = useState<AdminTransactionDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchTransaction(Number(id), ac.signal)
      .then((x) => {
        if (alive) setTx(x)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader
        title={`#${id}`}
        screenId="A10.02"
        actions={
          <Link
            to="/finance"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.nav.finance}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-2xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !tx ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-2xl rounded-lg border border-border-default bg-surface-2 p-4">
            <InfoRow label={t.finance.amount}>
              <span className={cn('font-mono text-lg tabular', tx.delta >= 0 ? 'text-success' : 'text-danger')}>
                {tx.delta >= 0 ? '+' : ''}
                {tx.delta.toLocaleString()}
              </span>
            </InfoRow>
            <InfoRow label={t.col.type}>{tx.kind || '—'}</InfoRow>
            <InfoRow label={t.col.name}>
              <Link to={`/users/${tx.user_id}`} className="text-accent hover:underline">
                {tx.username ? `@${tx.username}` : `#${tx.user_id}`}
              </Link>
            </InfoRow>
            <InfoRow label={t.finance.peer}>
              {tx.peer_id ? <MonoValue value={String(tx.peer_id)} /> : '—'}
            </InfoRow>
            <InfoRow label={t.finance.note}>{tx.note || '—'}</InfoRow>
            <InfoRow label={t.finance.idempotency}>
              {tx.idempotency_key ? <MonoValue value={tx.idempotency_key} truncate={10} /> : '—'}
            </InfoRow>
            <InfoRow label={t.col.time}>
              <RelativeTime value={tx.created_at} />
            </InfoRow>
          </div>
        )}
      </div>
    </>
  )
}
